import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { URLS } from 'constants/URL';
import { NotificationContext } from 'common/NotificationContainer/NotificationContainer';
import {
  useGetCall,
  useLazyGetCallWithURL,
  useLazyPostCall,
} from 'common/api/useApiCall';
import { OTPRequestType } from 'constants/URLEnums';

const DEFAULT_PAYLOAD = {
  tenant_id: '',
  service_id: '',
  service_instance_id: '',
  service_subscription_id: '',
  alertname: '',
  compute_resource_id: '',
  job: '',
  eks_namespace: '',
  mountpoint: '',
  startsAt: moment(new Date()),
  endsAt: moment(new Date()).add(1, 'hours'),
};

export default function useSilenceAlertsModel({
  selectedAlertLabels,
  handleOnSuccess,
  bulkSilence,
  includeDomain = false,
  silenceAlertsURL = URLS.silenceAlertsURL,
  tenantDomain = null,
}) {
  const [inputs, setInputs] = useState<any>(DEFAULT_PAYLOAD);
  const [comment, setComment] = useState('');
  const [range, setRange] = useState('1h');
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [entitiesMap, setEntitiesMap] = useState(null);

  const [openOTP, setOpenOTP] = useState(false);
  const [step, setStep] = useState(1);
  const [uuid, setUuid] = useState('');
  const disableSilence = selectedAlertLabels
    ? false
    : !inputs?.tenant_id &&
      !inputs?.service_id?.length &&
      !inputs?.alertname?.length &&
      !inputs?.service_instance_id?.length &&
      !inputs?.compute_resource_id &&
      !inputs?.job &&
      !inputs?.serviceIds?.length;

  const {
    response,
    postData: silenceAlert,
    isLoading,
    responseStatus,
  } = useLazyPostCall(silenceAlertsURL);
  const { response: tenantIdNamesResponse } = useGetCall(
    `${URLS.getTenantIdNames}with-id=true`,
  );

  const {
    response: entities,
    isLoading: entitiesLoading,
    postData: getEntities,
  } = useLazyGetCallWithURL();

  const {
    response: sendOTPRes,
    isLoading: sendOTPIsLoading,
    postData: sendOTP,
  } = useLazyPostCall(`${URLS.otp}purpose=${OTPRequestType.SILENCE_ALERT_OTP}`);

  const { addNotification } = useContext(NotificationContext);

  const handleChange = (e) => {
    if (Array.isArray(e) && bulkSilence) {
      setInputs((prev) => ({ ...prev, serviceIds: e.join(',') }));
    } else if (e.target.name === 'tenant_id') {
      setSelectedTenant(e.target.value);
      setInputs((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
        ...(bulkSilence ? { serviceIds: '' } : {}),
      }));
    } else {
      setInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }
  };

  useEffect(() => {
    if (tenantIdNamesResponse?.response?.length) {
      setTenants(
        tenantIdNamesResponse?.response?.map((_t) => ({
          _id: _t?._id,
          name: _t?.domain,
        })),
      );
    }
  }, [tenantIdNamesResponse]);

  useEffect(() => {
    setEntitiesMap(entities);
  }, [entities]);

  useEffect(() => {
    if (selectedTenant) {
      setEntitiesMap(null);
      getEntities(`${URLS.fetchTenantEntities}${selectedTenant?._id}`);
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedAlertLabels) {
      setInputs({
        ...selectedAlertLabels,
        startsAt: moment(new Date()),
        endsAt: moment(new Date()).add(1, 'hours'),
      });
      setRange('1h');
    }
  }, [selectedAlertLabels]);

  const handleTimeChange = (obj) => {
    setInputs((prev) => ({ ...prev, ...obj }));
  };

  useEffect(() => {
    if (sendOTPRes?.uuid) {
      setUuid(sendOTPRes?.uuid);
      setStep(2);
    }
  }, [sendOTPRes]);

  useEffect(() => {
    if (responseStatus === 200) {
      addNotification({
        severity: 'success',
        message: `${response?.message || 'Silence Alert successfully'}`,
      });
      setInputs(DEFAULT_PAYLOAD);
      setStep(1);
      setOpenOTP(false);
      handleOnSuccess();
    } else if (responseStatus > 200) {
      addNotification({
        severity: 'error',
        message: response?.message || 'Something went wrong',
      });
    }
  }, [response]);

  const createPayload = (inputs) => {
    let matchers = Object.keys(inputs)
      .filter((key) => key !== ('endsAt' as any) || key !== ('startsAt' as any))
      .map((key) =>
        inputs[key]?.length
          ? { name: key, value: inputs[key], isRegex: false, isEqual: true }
          : null,
      )
      .filter(Boolean);

    if (silenceAlertsURL === URLS.silenceAlertsV2URL) {
      // remove domain and tenantId property for V2 Alert
      matchers = matchers.filter(
        (matcher) => matcher.name !== 'domain' && matcher.name !== 'tenant_id',
      );
    }

    if (bulkSilence) {
      return {
        isBulk: true,
        serviceIds: inputs?.serviceIds,
        comment,
        startsAt: inputs.startsAt,
        endsAt: inputs.endsAt,
      };
    }
    const payload: any = {
      matchers,
      isBulk: false,
      tenantId:
        typeof inputs?.tenant_id === 'object'
          ? inputs?.tenant_id?._id
          : inputs?.tenant_id,
      comment,
      startsAt: inputs.startsAt,
      endsAt: inputs.endsAt,
    };

    // Add domain field only when called from SilenceAlertsV2 component.
    // The v2 API endpoint requires tenant domain (name) in addition to tenantId.
    // The original SilenceAlerts component (route: /silenced-alerts) doesn't need this field.
    if (includeDomain) {
      if (tenantDomain) {
        payload.domain = tenantDomain;
      } else {
        payload.domain =
          typeof inputs?.tenant_id === 'object'
            ? inputs?.tenant_id?.name
            : inputs?.tenant_id;
      }
    }

    return payload;
  };

  const handleConfirm = () => {
    setOpenOTP(true);
  };

  const otpSubmitHandler = (val) => {
    if (step === 1) {
      setComment(val?.reason);
      const payload = {
        comment: val?.reason,
        jiraId: val?.jiraId,
      };
      sendOTP(JSON.stringify(payload, null));
    } else {
      const payload: any = {
        otp: val,
        'request-id': uuid,
        tenantId:
          typeof inputs?.tenant_id === 'object'
            ? inputs?.tenant_id?._id
            : inputs?.tenant_id,
        silencePayload: createPayload(inputs),
      };

      // Add domain field only when called from SilenceAlertsV2 component.
      // The v2 API endpoint (silence-v2) requires tenant domain (name) in the top-level payload.
      // The original SilenceAlerts component (route: /silenced-alerts) uses the v1 endpoint
      // which doesn't require this field.
      if (includeDomain) {
        if (tenantDomain) {
          payload.domain = tenantDomain;
        } else {
          payload.domain =
            typeof inputs?.tenant_id === 'object'
              ? inputs?.tenant_id?.name
              : inputs?.tenant_id;
        }
      }

      silenceAlert(JSON.stringify(payload));
    }
  };

  useEffect(() => {
    if (range === 'Custom') {
      return;
    }
    const st = moment(new Date());
    let et;
    switch (range) {
      case '1h':
        et = moment(new Date()).add(1, 'hours');
        break;
      case '3h':
        et = moment(new Date()).add(3, 'hours');
        break;
      case '6h':
        et = moment(new Date()).add(6, 'hours');
        break;
      case '12h':
        et = moment(new Date()).add(12, 'hours');
        break;
      case '24h':
        et = moment(new Date()).add(24, 'hours');
        break;
      default:
    }
    handleTimeChange({ startsAt: st, endsAt: et });
  }, [range]);

  const handleDateToggle = (e, v) => {
    if (v) {
      setRange(v);
    }
  };

  const updateTimeUsingNativePicker = (start, end) => {
    handleTimeChange({ startsAt: start, endsAt: end });
  };

  const serviceIdOptions = useMemo(() => {
    return (
      entitiesMap?.service?.map((v) => ({
        label: `${v._id} (${v.name})`,
        value: v._id,
      })) || []
    );
  }, [entitiesMap]);

  return {
    inputs,
    isLoading,
    openOTP,
    sendOTPIsLoading,
    step,
    disableSilence,
    tenants,
    entitiesMap,
    selectedTenant,
    entitiesLoading,
    handleChange,
    setOpenOTP,
    setStep,
    handleConfirm,
    otpSubmitHandler,
    handleDateToggle,
    updateTimeUsingNativePicker,
    range,
    serviceIdOptions,
  };
}
